import React from 'react';
import type { CollapsibleComponentProps } from '../common-types';
import { Collapsible } from './Collapsible';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';

/**
 * Displays an inline error message with an error icon.
 * When {@link InlineComponentWithCollapsible.collapsible collapsible} is true,
 * the content is rendered inside a collapsible section.
 */
export function InlineError(
  props: Readonly<InlineComponentProps>
): React.JSX.Element {
  return <InlineComponent {...props} type="ERROR" />;
}

/**
 * Displays an inline warning message with an information icon.
 * When {@link InlineComponentWithCollapsible.collapsible collapsible} is true,
 * the content is rendered inside a collapsible section.
 */
export function InlineWarning(
  props: Readonly<InlineComponentProps>
): React.JSX.Element {
  return <InlineComponent {...props} type="WARNING" />;
}

type ComponentType = 'ERROR' | 'WARNING';

interface InlineComponentBaseProps extends CollapsibleComponentProps {
  text: string | React.JSX.Element;
  type: ComponentType;
}

export type InlineComponentWithoutCollapsible = InlineComponentBaseProps & {
  collapsible?: false;
  title?: string;
};
export type InlineComponentWithCollapsible = InlineComponentBaseProps & {
  collapsible: true;
  title: string;
  'aria-label': string;
};

export type InlineComponentWithTypeProps =
  | InlineComponentWithCollapsible
  | InlineComponentWithoutCollapsible;

type InlineComponentProps =
  | Omit<InlineComponentWithoutCollapsible, 'type'>
  | Omit<InlineComponentWithCollapsible, 'type'>;

const inlineComponentSettings: Record<
  ComponentType,
  {
    icon: ValidIconName;
    className: string;
    role: 'alert' | 'status';
  }
> = {
  WARNING: {
    icon: 'shui_information',
    className: 'warning',
    role: 'status'
  },
  ERROR: {
    icon: 'shui_error',
    className: 'error',
    role: 'alert'
  }
};

function InlineComponent(props: Readonly<InlineComponentWithTypeProps>) {
  if (!props.collapsible) {
    return <InlineComponentNotCollapsible {...props} />;
  }

  return <InlineComponentCollapsible {...props} />;
}

function InlineComponentNotCollapsible({
  title,
  text,
  type
}: Readonly<InlineComponentWithoutCollapsible>) {
  const settings = inlineComponentSettings[type];

  return (
    <div
      className={`ehs-inline-container ${settings.className}`}
      role={settings.role}
    >
      {title && (
        <div className="ehs-inline-title font-18">
          <Icon
            iconName={settings.icon}
            className="ehs-inline-title-icon"
            aria-label={undefined}
          />
          <div className="ehs-inline-title-text">{title}</div>
        </div>
      )}
      <div className="ehs-inline-text font-16">{text}</div>
    </div>
  );
}

function InlineComponentCollapsible({
  title,
  text,
  type,
  openInitially,
  collapsed,
  setCollapsed,
  'aria-label': ariaLabel
}: Readonly<InlineComponentWithCollapsible>) {
  const settings = inlineComponentSettings[type];

  return (
    <div className={`ehs-inline-container ${settings.className}`}>
      <Collapsible
        className="ehs-inline-collapsible font-14"
        triggerText={title}
        openInitially={openInitially}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        aria-label={ariaLabel}
        contentRole="region"
        lazyRender={false}
      >
        <div className="ehs-inline-text font-12">{text}</div>
      </Collapsible>
    </div>
  );
}
